// @flow
import type { PaymentActionType } from './actions';
import config from '../../config';

const getPaymentInfoInitialState = (): Object => {
  if (config.paymentProvider === 'stripe') {
    return {
      type: 'STRIPE',
      stripeCustomerId: '',
      stripeIntentId: '',
      stripeIntentSecret: '',
      stripePaymentMethodId: '',
      stripeTimeout: false,
      stripeRedirectUrl: '',
      isThreeDSecureAuthentModalOpen: false,
    };
  }

  return {
    type: 'BRAINTREE',
    braintreeNonce: '',
  };
};

export const initialState: PaymentStateType = {
  ...getPaymentInfoInitialState(),
  history: [],
  paymentAuthenticationResult: 'PENDING',
  paymentAuthentications: [],
};

export const paymentReducer = (state: PaymentStateType = initialState, action: PaymentActionType) => {
  switch (action.type) {
    case 'SIGNUP_SET_PAYMENT_INFO':
      return {
        ...state,
        ...action.payload.paymentInfo,
      };
    case 'SIGNUP_SET_PAYMENT_BRAINTREE_CLIENT_TOKEN':
      return {
        ...state,
        ...action.payload,
      };
    case 'SIGNUP_SET_PAYMENT_STRIPE_CLIENT_TOKEN':
      return {
        ...state,
        ...action.payload,
      };
    case 'FETCH_PAYMENTS_SUCCESS': {
      const history = action.payments.map<Object>(paymentItem => ({ ...paymentItem, folded: true }));

      return {
        ...state,
        history,
      };
    }
    case 'STRIPE_TIMEOUT':
      return {
        ...state,
        stripeTimeout: true,
      };
    case 'CLEAR_STRIPE_ERROR':
      return {
        ...state,
        stripeTimeout: false,
      };
    case 'SET_PAYMENT_AUTHENTICATIONS':
      return {
        ...state,
        paymentAuthentications: action.payload.paymentAuthentications,
      };
    case 'SET_PAYMENT_AUTHENTICATION_RESULT':
      return {
        ...state,
        paymentAuthenticationResult: action.payload.paymentAuthenticationResult,
      };
    case 'SET_STRIPE_REDIRECT_URL':
      return {
        ...state,
        stripeRedirectUrl: action.payload.stripeRedirectUrl,
      };
    case 'SET_IS_THREE_D_SECURE_AUTHENT_MODAL_OPEN':
      return {
        ...state,
        isThreeDSecureAuthentModalOpen: action.payload.isModalOpen,
      };
    default:
      return state;
  }
};

export type PaymentResponseType = {|
  type: 'BRAINTREE' | 'STRIPE',
  braintreeNonce?: string,
  stripeCustomerId?: string,
  stripeIntentId?: string,
  stripeIntentSecret?: string,
  stripeIntentStatus?: string,
  stripePaymentMethodId?: string,
|};

export type PaymentAuthentication = {
  amount: number,
  billingAccountOuid: number,
  notificationMaxSendDate: number,
  notificationOuid: string,
  primaryResourceValue: string,
  processName: string,
  stripeIntentId?: string,
  stripeRedirectUrl?: string,
  subscriptionOuid: string,
  transactionId: string,
};

export type StripePaymentMethodType = {
  card: {
    addressCity: string,
    addressCountry: string,
    addressLine1: string,
    addressLine2: string,
    addressState: string,
    addressZip: string,
    brand: string,
    cardId: string,
    country: string,
    expMonth: number,
    expYear: number,
    funding: string,
    isApplePayCard: boolean,
    last4: string,
    name: string,
  },
  created: number,
  id: string,
  livemode: boolean,
};

export type PaymentAuthenticationResultType = 'FAILURE' | 'PENDING' | 'PENDING_AUTHENT' | 'SUCCESS';

export type PaymentStateType = {|
  ...PaymentResponseType,
  braintreeClientToken?: string,
  history: Object[],
  isThreeDSecureAuthentModalOpen: boolean,
  paymentAuthenticationResult: PaymentAuthenticationResultType,
  paymentAuthentications: PaymentAuthentication[],
  stripeRedirectUrl: string,
|};

export type PaymentInfoType = {|
  type: 'BRAINTREE' | 'STRIPE',
  braintreeNonce?: string,
  stripePaymentMethod?: StripePaymentMethodType,
|};

export type PaymentMean =
  | {|
      name: string,
      cardNumber: string,
      holder: string,
      expirationDate: string,
      token: string,
    |}
  | PaymentResponseType;
